/**
 * Human-readable names + descriptions for sandbox provision-timing rows.
 *
 * The backend stores raw keys (`phase4_deploy`, `clone_config`, `app_up`,
 * `web-build`, `deps`, …) on `sandbox_provision_timings`. This maps each known
 * key to a friendly label + one-line description (surfaced as a hover tooltip),
 * with a sensible fallback for app-defined docker-compose service names that we
 * can't enumerate ahead of time.
 */

import { provisionTimingGroupOf } from '@chatsdet/types';
import type { WaterfallGroup, WaterfallSubEntry } from '@chatsdet/ui';

export interface TimingMeta {
  name: string;
  description: string;
}

/**
 * The two phases the Gantt groups a provision into, so the chart shows an
 * explicit break between "sandbox ready" (VM up, repo on branch — the sub-30s
 * target) and "built" (images built, app healthy — the deploy floor, out of
 * scope for that target).
 */
export const provisionPhaseGroups: WaterfallGroup[] = [
  {
    id: 'ready',
    label: 'Ready',
    tone: 'agent',
    description:
      'Instance booted and the repo is on the target branch — the sandbox is ready and the build is about to start.',
  },
  {
    id: 'build',
    label: 'Build',
    tone: 'muted',
    description:
      'Build the images and bring the app up to healthy. This is the deploy floor — out of scope for the sub-30s time-to-ready target.',
  },
];

/**
 * Which phase group a timing row belongs to. Container builds (`deps`,
 * `web-build`), the health gate and the app/startup steps are the *build*;
 * instance allocation, SSM, log group, routing, clone, config and agent boot all
 * get the sandbox *ready*.
 *
 * Delegates to `@chatsdet/types` because the API applies the same split when it
 * summarizes a run for a non-superadmin — a step classified one way here and the
 * other way there would make the two views disagree about the same provision.
 */
export const timingPhaseOf = provisionTimingGroupOf;

/** Keyed by the raw `step` value. Covers every platform-emitted step. */
const STEP_META: Record<string, TimingMeta> = {
  // phase rows
  phase1_allocate: {
    name: 'Allocate instance',
    description:
      'Claim a warm pool member (stopped or running) or launch a fresh EC2 instance. A stopped-pool claim is measured inside this phase.',
  },
  stopped_pool_claim: {
    name: 'Claim warm member',
    description:
      'Start a pre-built, stopped pool member and remap it to this sandbox — the fast path that skips the cold boot.',
  },
  phase2_ssm_ready: {
    name: 'Wait for SSM',
    description:
      'Wait for the SSM agent to come online so commands can run on the instance. Skipped entirely on a pool claim, which is already SSM-ready.',
  },
  phase3_log_group: {
    name: 'Create log group',
    description: "Create the instance's CloudWatch log group.",
  },
  phase4_deploy: {
    name: 'Deploy',
    description: 'Clone the repo, start the agent + app containers, and run the startup script.',
  },
  phase5_routing: {
    name: 'Attach routing',
    description: 'Create the load balancer target group and listener rule that give the sandbox its URL.',
  },
  phase6_build: {
    name: 'Initial build',
    description: 'Run the build script — nginx config, compose up, and the startup hook.',
  },
  phase7_health: {
    name: 'Health gate',
    description:
      'Wait for the app (and agent, if present) to answer as healthy before the sandbox is published as running.',
  },
  base_seeds: {
    name: 'Base seeds',
    description: "Run the template's runOnProvision actions once, before the sandbox becomes leasable.",
  },
  capture_artifacts: {
    name: 'Capture artifacts',
    description:
      'Push the prebuilt image and build cache so later provisions can skip the rebuild. Only does work on a verification provision.',
  },
  // health_stage rows (nested inside phase7_health)
  stage1_app_health: {
    name: 'App health',
    description: "Poll the app's /health endpoint on localhost via SSM.",
  },
  stage2_alb_target: {
    name: 'Load balancer target',
    description: 'Wait for the load balancer to report the instance as a healthy target.',
  },
  stage3_agent_reachable: {
    name: 'Agent reachable',
    description: 'Wait for the in-sandbox coding agent to answer (agent sandboxes only).',
  },
  // deploy_step rows
  instance_boot: {
    name: 'Instance boot',
    description:
      'Wait for the EC2 instance to boot, run cloud-init, and bring the SSM agent online before the deploy script can run. ~0 on a pre-warmed pool instance; dominant on a cold launch.',
  },
  clone_config: {
    name: 'Clone & configure',
    description: 'Clone the repository and generate the container compose config (legacy umbrella step).',
  },
  net_create: {
    name: 'Create network',
    description: 'Create the per-sandbox docker network.',
  },
  repos_cloned: {
    name: 'Clone repos',
    description: 'git clone the template repositories into the workspace.',
  },
  config_setup: {
    name: 'Write config',
    description: 'Generate compose/Dockerfile/nginx config and run the pre-docker setup script.',
  },
  agent_up: {
    name: 'Start agent',
    description: 'Boot the in-container Cofactor agent.',
  },
  app_up: {
    name: 'Start app',
    description: 'Build images and start the app + api services (docker compose up).',
  },
  startup: {
    name: 'Startup script',
    description: "Run the template's startup.sh.",
  },
  seed: {
    name: 'Seed data',
    description: "Run the template's seed.sh (first launch only).",
  },
  // container rows (known build steps)
  deps: {
    name: 'Install dependencies',
    description: 'Install npm/bun packages during the image build.',
  },
  'web-build': {
    name: 'Build web app',
    description: 'Compile the frontend bundle during the image build.',
  },
};

/** Synthetic row representing total − deploy (pre-deploy work with no row). */
export const OTHER_SETUP_META: TimingMeta = {
  name: 'Other setup',
  description:
    'Pre-deploy setup — EC2 launch, SSM readiness, log group and load balancer attach.',
};

/** Prettify an unknown raw key: `mc-init` → `Mc init`, `my_service` → `My service`. */
function prettify(step: string): string {
  const spaced = step.replace(/[-_]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Display label for a `cache_restore` row's outcome. */
const CACHE_OUTCOME_LABEL: Record<string, string> = {
  hit: 'Hit',
  prime: 'Prime',
  miss: 'Miss',
};

/**
 * Tooltip copy per outcome — the whole reason these rows exist is that only a
 * `miss` on the build service triggers the one-shot rebuild, so callers need to
 * see that distinction, not just "restored".
 */
const CACHE_OUTCOME_DESCRIPTION: Record<string, string> = {
  hit: 'Full restore — the one-shot install was skipped.',
  prime: 'Full restore, but the one-shot install still ran warm.',
  miss: 'Nothing usable restored — this service rebuilt from scratch.',
};

/**
 * A `cache_restore` row's service name is reused from `STEP_META` (`deps`,
 * `web-build`, …) where known, since those are the same compose services
 * `container` rows describe. The outcome is appended so the label is legible on
 * its own — it's the only reason a per-service row exists instead of the single
 * blended `cache_restore` lap.
 */
function describeCacheRestore(service: string, outcome: string | null | undefined): TimingMeta {
  const serviceName = STEP_META[service]?.name ?? prettify(service);
  const outcomeLabel = outcome ? (CACHE_OUTCOME_LABEL[outcome] ?? prettify(outcome)) : 'Unknown';
  const outcomeDescription = outcome
    ? (CACHE_OUTCOME_DESCRIPTION[outcome] ?? `Cache restore outcome: ${outcome}.`)
    : 'Cache restore outcome not recorded.';
  return { name: `${serviceName} · ${outcomeLabel}`, description: outcomeDescription };
}

/**
 * Bar tone for a `cache_restore` subentry. `hit`/`prime` both restored
 * successfully, so they read as cached; `miss` is the one outcome that costs the
 * deploy a full rebuild, so it gets the more attention-worthy tone.
 */
export function cacheRestoreTone(outcome: string | null | undefined): WaterfallSubEntry['tone'] {
  return outcome === 'miss' ? 'warning' : 'cached';
}

/**
 * Resolve a friendly name + description for a timing row. Unknown `container`
 * service names fall back to a prettified key + generic description. `outcome`
 * only applies to `cache_restore` rows, whose label is the service name plus
 * its hit/prime/miss outcome rather than a static lookup.
 */
export function describeTiming(kind: string, step: string, outcome?: string | null): TimingMeta {
  if (kind === 'cache_restore') return describeCacheRestore(step, outcome);
  const known = STEP_META[step];
  if (known) return known;
  if (kind === 'container') {
    return { name: prettify(step), description: 'Container build step.' };
  }
  return { name: prettify(step), description: '' };
}
